/* eslint-disable no-await-in-loop */
/* eslint-disable class-methods-use-this */
import { Injectable } from "@angular/core";
import {
    FeatureService,
    NavigatorService,
    ZoneChangeService,
    FilterService,
    HashtagService,
    BaseUserService,
    filterUniqueArrayByID,
    Feature,
    POI,
    Hashtag,
    MattertagData,
    PoiType,
    PoiService,
} from "@smarterplan/ngx-smarterplan-core";
import { Subject } from "rxjs";
import { UpdateFeatureInput } from "src/app/API.service";

@Injectable({
    providedIn: "root",
})
export class MuseumFeatureService {
    featureUpdated: Subject<{
        space: Feature[];
        zone: Feature[];
    }> = new Subject();

    featureTags: Subject<Feature[]> = new Subject();

    featureFiltered: Feature[] = [];

    updateDone: Subject<boolean> = new Subject();

    zoneIDFilter: string;

    currentSpaceID: string;

    updating: Subject<boolean> = new Subject();

    isUpdating = false;

    featureDomainFilter: string[] = null;

    public currentHashtags: Hashtag[] = [];

    public currentFeaturesSpaceZoneMap: {
        space: Feature[];
        zonesMap: Map<string, Feature[]>;
    } = {
            space: [],
            zonesMap: new Map(),
        };

    createObservable: Subject<Feature> = new Subject();

    deleteObservable: Subject<Feature> = new Subject();

    constructor(
        private featureService: FeatureService,
        private navigatorService: NavigatorService,
        private zoneChangeService: ZoneChangeService,
        private filterService: FilterService,
        private hashtagService: HashtagService,
        private userService: BaseUserService,
        private poiService: PoiService,
    ) {
        this.zoneChangeService.zoneChange.subscribe((zone) => {
            if (!this.navigatorService.currentSpaceID) {
                this.featureTags.next(null);
                this.featureUpdated.next({ space: [], zone: null });
                this.zoneIDFilter = null;
                this.featureDomainFilter = null;
                this.currentFeaturesSpaceZoneMap = {
                    space: [],
                    zonesMap: new Map(),
                };
                this.currentSpaceID = this.navigatorService.currentSpaceID;
                return;
            }
            if (zone.id !== this.zoneIDFilter) {
                this.currentSpaceID = this.navigatorService.currentSpaceID;
                this.zoneIDFilter = zone.id;
                if (!this.currentFeaturesSpaceZoneMap.zonesMap.has(zone.id)) {
                    this.zoneIDFilter = zone.id;
                    // console.log("going to init PI for zone for new space");
                    this.initFeatures();
                }
            }
        });
        this.updateDone.subscribe(() => {
            if (this.currentSpaceID) {
                this.initFeatures();
            }
        });

        this.deleteObservable.subscribe((feature) => {
            if (this.currentSpaceID) {
                this.updateDueToDelete(feature);
            }
        });

        this.createObservable.subscribe((feature) => {
            if (this.currentSpaceID) {
                this.updateDueToCreate(feature);
            }
        });
    }

    async initFeatures(): Promise<void> {
        if (!this.currentSpaceID || !this.userService.cu) {
            return;
        }
        this.updating.next(true);
        this.isUpdating = true;
        let featureList =
            await this.featureService.getFeaturesBySpaceWithoutFiltering(
                this.currentSpaceID,
            );
        featureList = this.filterService.filterFeaturesForMissionWithoutZone(
            featureList,
            this.userService.currentMission(this.currentSpaceID),
        );
        this.currentHashtags = await this.hashtagService.getHashtagsForSpace(
            this.currentSpaceID,
        );
        // console.log(this.currentHashtags);
        if (featureList.length > 0) {
            let filteredFeatures = [...featureList];
            if (this.featureDomainFilter) {
                filteredFeatures = filteredFeatures.filter((feature) =>
                    this.featureDomainFilter.find(
                        (domain) =>
                            feature.domain && domain === feature.domain.id,
                    ),
                );
            }
            this.currentFeaturesSpaceZoneMap.space = filteredFeatures.sort(
                (a, b) => a.index - b.index,
            );
            await this.splitFeaturesPerZone();
            this.featureUpdated.next({ space: [], zone: [] }); // for museum collection list the values do not matter
        } else {
            this.featureUpdated.next({ space: [], zone: [] }); // for lateral menu
            await this.splitFeaturesPerZone(); // to set empty arrays for zones
        }
        this.updating.next(false);
        this.isUpdating = false;
    }

    async getFeaturesForSpace(
        spaceID: string,
        keepArchived = false,
    ): Promise<Feature[]> {
        return this.featureService.getFeaturesForSpace(spaceID, keepArchived);
    }

    async createFeature(feature: Feature): Promise<Feature> {
        return this.featureService.createFeature(feature);
    }

    async getFeature(id: string): Promise<Feature> {
        return this.featureService.getFeature(id);
    }

    async createPOIForFeature(
        feature: Feature,
        tagData: MattertagData,
        poiInput: POI = null,
    ): Promise<POI> {
        let input: POI;
        if (poiInput) {
            input = poiInput;
            input.coordinate = tagData.getCoordinateString();
            input.type = PoiType.DATA;
            input.elementID = feature.id;
            input.metadata = tagData.getMetadataString();
            input.matterportSweepID = tagData.getSweepID();
        } else {
            input = {
                coordinate: tagData.getCoordinateString(),
                type: PoiType.DATA,
                elementID: feature.id,
                metadata: tagData.getMetadataString(),
                matterportSweepID: tagData.getSweepID(),
            };
        }
        const poi = await this.poiService.createPOIForSpaceID(input);
        return poi;
    }

    async readFeature(id: string): Promise<Feature> {
        return this.featureService.getFeature(id);
    }

    async deleteFeature(feature: Feature): Promise<Feature> {
        return this.featureService.deleteFeature(feature);
    }

    async updateFeature(feature: UpdateFeatureInput): Promise<Feature> {
        return this.featureService.updateFeature(feature);
    }

    async getFirstFeatureForSpace(spaceID: string): Promise<Feature> {
        return this.featureService.getFirstFeatureForSpace(spaceID);
    }

    async updateIconForAllFeaturesInSpace(
        tagIcon: string,
        spaceID: string,
        categoryID: string = null,
    ): Promise<{ features: Feature[]; pois: POI[]; }> {
        return this.featureService.updateIconForAllFeaturesInSpace(
            tagIcon,
            spaceID,
            categoryID,
        );
    }

    async getCreatedIndicesForFeaturesInSpace(
        spaceID: string,
    ): Promise<number[]> {
        return this.featureService.getCreatedIndicesForFeaturesInSpace(spaceID);
    }

    /** Return true if the current user has archived this feature */
    isArchived(feature: Feature): boolean {
        return this.featureService.isArchived(feature);
    }

    setArchived(feature: Feature, archived: boolean) {
        return this.featureService.setArchived(feature, archived);
    }

    async splitFeaturesPerZone(): Promise<void> {
        let availableFeatureTags = [];
        await Promise.all(
            this.navigatorService.zonesForUserForSpace.map(async (zone) => {
                if (zone.sweepIDs && zone.sweepIDs.length > 0) {
                    const featuresZone =
                        await this.filterService.filterObjectsForZone(
                            this.currentFeaturesSpaceZoneMap.space,
                            null,
                            zone,
                        );
                    this.currentFeaturesSpaceZoneMap.zonesMap.set(
                        zone.id,
                        featuresZone.sort((a, b) => a.index - b.index),
                    );
                    availableFeatureTags = [
                        ...availableFeatureTags,
                        ...featuresZone,
                    ];
                }
            }),
        );
        availableFeatureTags = filterUniqueArrayByID(availableFeatureTags);
        // since we have just filtered by zone we update the whole space features
        this.currentFeaturesSpaceZoneMap.space = availableFeatureTags;
        this.featureTags.next(availableFeatureTags);
    }

    async updateDueToCreate(feature: Feature): Promise<void> {
        this.currentFeaturesSpaceZoneMap.space.unshift(feature);
        this.currentFeaturesSpaceZoneMap.zonesMap
            .get(this.zoneIDFilter)
            .unshift(feature);
        this.featureUpdated.next({ space: [], zone: [] }); // for lateral menu back compatibility
    }

    async updateDueToDelete(deletedFeature: Feature): Promise<void> {
        const feature = this.currentFeaturesSpaceZoneMap.space.find(
            (_feature) => _feature.id === deletedFeature.id,
        );
        if (feature) {
            const index =
                this.currentFeaturesSpaceZoneMap.space.indexOf(feature);
            this.currentFeaturesSpaceZoneMap.space.splice(index, 1);
        }
        Array.from(this.currentFeaturesSpaceZoneMap.zonesMap.keys()).forEach(
            (zoneID) => {
                const featureToDelete =
                    this.currentFeaturesSpaceZoneMap.zonesMap
                        .get(zoneID)
                        .find((_feature) => _feature.id === deletedFeature.id);
                if (featureToDelete) {
                    const index = this.currentFeaturesSpaceZoneMap.zonesMap
                        .get(zoneID)
                        .indexOf(featureToDelete);
                    this.currentFeaturesSpaceZoneMap.zonesMap
                        .get(zoneID)
                        .splice(index, 1);
                }
            },
        );
        this.featureUpdated.next({ space: [], zone: [] }); // to update collection list
    }

    async getModel3dAndSweep(object: Feature): Promise<{
        model3D: string;
        sweep: string;
        rotation: { x: number; y: number; };
    } | void> {
        return this.featureService.getModel3dAndSweep(object);
    }

    setCoverImage(feature: Feature) {
        this.featureService.setCoverImage(feature);
    }
}
